import { createFileRoute } from "@tanstack/react-router";
import { SpokePage, buildSpokeHead, type SpokePageConfig } from "@/components/SpokePage";

const CONFIG: SpokePageConfig = {
  slug: "roofing-web-design-colorado",
  metaTitle: "Roofing Web Design in Colorado | Sites That Convert Storm Leads",
  metaDescription:
    "Custom roofing web design across Colorado — fast, mobile-first sites built for hail-damage leads and insurance-claim flow. Call (719) 419-3935.",
  breadcrumbs: [
    { label: "Home", to: "/" },
    { label: "Roofing SEO", to: "/roofing-seo-colorado" },
  ],
  currentLabel: "Roofing Web Design Colorado",
  eyebrow: "Roofing Web Design — Colorado",
  h1: (
    <>
      Roofing <span className="text-brand-gold">Web Design</span> in Colorado
    </>
  ),
  heroDescription:
    "Hand-coded roofing sites built for the moment a Colorado homeowner walks outside, sees hail damage, and pulls out their phone.",
  intro:
    "A Colorado roofing website's job is simple: turn a hail-damaged homeowner's panicked Google search into a booked free inspection. We hand-code fast, mobile-first roofing sites with sticky click-to-call, dedicated pages for hail damage / insurance claims / metal roofing / commercial / repair, manufacturer credentials front and center, and conversion-tested inspection forms. No bloated templates, no slow builders.",
  definition: {
    term: "Roofing web design in Colorado",
    body: "Roofing web design is the practice of building conversion-focused, mobile-first websites for roofing companies — with dedicated service pages, insurance-claim flows, manufacturer credential signals, and proper local SEO baked in from day one.",
  },
  whatWeDo: [
    { title: "Hand-coded for speed", body: "Sub-2-second mobile load, 95+ Core Web Vitals — critical for storm-season ad-driven traffic." },
    { title: "One page per service line", body: "Hail damage, insurance claim, asphalt shingle, metal, TPO, repair, replacement — each ranks independently." },
    { title: "Manufacturer credentials front and center", body: "GAF Master Elite, Owens Corning Platinum, CertainTeed Select — surfaced in design and marked up in schema." },
    { title: "Insurance-claim landing pages", body: "Dedicated pages built for the insurance-claim buying process, with documentation guides and adjuster-meeting flows." },
    { title: "Inspection-booking forms", body: "Conversion-tested forms with the minimum fields needed to dispatch an inspector." },
    { title: "Schema and SEO from launch", body: "RoofingContractor, Service, FAQPage, Review, AggregateRating schema on every page from day one." },
  ],
  whyThisMarket: [
    {
      heading: "Storm traffic spikes will crush a slow site",
      body: "After a Front Range hail event, traffic to roofing sites can 5–10x within hours. Templated sites on shared hosting routinely crash. Hand-coded sites on edge infrastructure handle the spike.",
    },
    {
      heading: "Trust signals matter more in roofing than almost any vertical",
      body: "Homeowners are about to spend $20K–$40K of insurance money. Manufacturer certifications, BBB rating, real Colorado job photos, and review counts must be visible above the fold — not buried.",
    },
    {
      heading: "Most roofing sites are template-built and identical",
      body: "We've audited hundreds of Colorado roofing sites — the majority use the same 3–4 templates from industry-specific agencies. Custom design isn't vanity; it's how you stand out when 10 roofers chase the same insurance check.",
    },
  ],
  relatedLinks: [
    { to: "/roofing-seo-colorado", label: "Roofing SEO in Colorado" },
    { to: "/colorado-springs-web-design", label: "How our web design process works" },
    { to: "/roofing-google-maps-colorado", label: "Roofing Google Maps optimization" },
    { to: "/roofing-ppc-colorado", label: "Roofing PPC & storm-season ads" },
    { to: "/colorado-springs", label: "Colorado Springs digital marketing agency" },
    { to: "/web-design-denver-colorado", label: "Denver web design" },
    { to: "/case-studies", label: "Colorado roofing case studies" },
    { to: "/wix-vs-custom-website-colorado-roofing", label: "Wix vs custom website for Roofing" },
    { to: "/squarespace-vs-custom-website-colorado-roofing", label: "Squarespace vs custom website for Roofing" },
    { to: "/godaddy-vs-custom-website-colorado-roofing", label: "GoDaddy vs custom website for Roofing" },
    { to: "/web-design", label: "Our web design service (all industries)" },
    { to: "/pricing", label: "Web design pricing" },
  ],
  faqs: [
    { q: "How much does a roofing website cost in Colorado?", a: "Most Colorado roofing site builds run $5,500–$12,500 depending on number of service pages, insurance-claim flows, and CRM integrations (JobNimbus, AccuLynx, etc.). All pricing is published openly." },
    { q: "How long does it take to build a roofing website?", a: "Typical timeline is 5–7 weeks for a 15–25 page roofing site with full service, service-area, and insurance-claim pages. Multi-location roofing companies can take 8–10 weeks." },
    { q: "Will the new site protect my existing rankings?", a: "Yes — we build proper 301 redirects from every old URL, preserve your domain, and ship with full schema and on-page SEO from launch so rankings stay or improve." },
    { q: "Do you only serve Colorado Springs roofers?", a: "No — we serve roofing companies across all of Colorado: Denver, Aurora, Fort Collins, Pueblo, Castle Rock, Parker, Boulder, and the Front Range." },
  ],
  serviceName: "Roofing Web Design in Colorado",
  serviceDescription:
    "Hand-coded, mobile-first websites for roofing companies across Colorado. Built for storm-season traffic and insurance-claim conversion.",
  areaServed: "Colorado",
};

export const Route = createFileRoute("/roofing-web-design-colorado")({
  head: () => buildSpokeHead(CONFIG),
  component: () => <SpokePage config={CONFIG} />,
});
